import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Strategy configurations
const STRATEGY_CONFIGS = {
  'CobraLong': {
    name: 'Cobra Long Strategy',
    description: 'Long position with Cobra confirmation',
    type: 'LONG',
    color: '#00ff88',
    bgColor: 'from-green-500/20 to-green-600/10',
    borderColor: 'border-green-500/30',
    icon: '↗️',
    conditions: [
      {
        id: 'C1',
        name: 'Entry Condition',
        type: 'timed',
        signals: [
          { id: 's1', name: 'LUX_SHORT_ACTIVE', type: 'early', required: true, description: 'Early signal trigger' },
          { id: 's2', name: 'COBRA_LONG_ACTIVE', type: 'confirmation', required: true, description: 'Confirmation signal' },
        ],
        minMinutes: 1,
        maxMinutes: 10,
      }
    ],
    exitSignal: 'LUX_SHORT_ACTIVE',
  },
  'LuxShort': {
    name: 'Lux Short Strategy',
    description: 'Short position with Lux signals',
    type: 'SHORT',
    color: '#ff4757',
    bgColor: 'from-red-500/20 to-red-600/10',
    borderColor: 'border-red-500/30',
    icon: '↘️',
    conditions: [
      {
        id: 'C1',
        name: 'Entry Condition',
        type: 'timed',
        signals: [
          { id: 's1', name: 'LUX_LONG_ACTIVE', type: 'early', required: true, description: 'Early signal trigger' },
          { id: 's2', name: 'LUX_SHORT_ACTIVE', type: 'confirmation', required: true, description: 'Confirmation signal' },
        ],
        minMinutes: 1,
        maxMinutes: 10,
      }
    ],
    exitSignal: 'LUX_LONG_ACTIVE',
  },
  'SmartBand': {
    name: 'Smart Band Strategy',
    description: 'Smart money with band confirmation',
    type: 'LONG',
    color: '#ffa502',
    bgColor: 'from-amber-500/20 to-amber-600/10',
    borderColor: 'border-amber-500/30',
    icon: '⚡',
    conditions: [
      {
        id: 'C1',
        name: 'Entry Condition',
        type: 'simultaneous',
        signals: [
          { id: 's1', name: 'SMART_BAND_12_ACTIVE', type: 'primary', required: true, description: 'Smart band signal' },
          { id: 's2', name: 'SMART_TRAIL_2_ACTIVE', type: 'secondary', required: false, description: 'Smart trail confirmation' },
        ],
        tolerance: 60,
      }
    ],
    exitSignal: 'SMART_BAND_12_ACTIVE',
  },
};

const StrategyProgressTracker = ({ strategyId = 'CobraLong', alerts = [] }) => {
  const [expanded, setExpanded] = useState(true);
  const [strategy, setStrategy] = useState(null);
  const [progress, setProgress] = useState({
    completedSignals: [],
    pendingSignals: [],
    isEntryReady: false,
    isInPosition: false,
  });
  const [timeRemaining, setTimeRemaining] = useState({});

  useEffect(() => {
    const config = STRATEGY_CONFIGS[strategyId];
    if (config) {
      setStrategy(config);
      initializeProgress(config);
    }
  }, [strategyId]);

  useEffect(() => {
    if (!strategy) return;
    
    const receivedSignals = alerts
      .filter(alert => alert.raw?.signal)
      .map(alert => ({
        signal: alert.raw.signal,
        timestamp: new Date(alert.timestamp).getTime(),
        price: alert.raw.price,
        type: alert.signal_type,
      }));
    
    updateProgress(receivedSignals);
  }, [alerts, strategy]);

  useEffect(() => {
    const interval = setInterval(() => {
      updateTimeRemaining();
    }, 1000);
    
    return () => clearInterval(interval);
  }, [progress]);

  const initializeProgress = (strategyConfig) => {
    const allSignals = [];
    strategyConfig.conditions.forEach(condition => {
      condition.signals.forEach(signal => {
        allSignals.push({
          ...signal,
          conditionId: condition.id,
          conditionType: condition.type,
        });
      });
    });
    
    setProgress({
      completedSignals: [],
      pendingSignals: allSignals,
      isEntryReady: false,
      isInPosition: false,
    });
  };

  const updateProgress = (receivedSignals) => {
    if (!strategy) return;
    
    const completed = [];
    const pending = [];
    let isReady = false;
    
    strategy.conditions.forEach(condition => {
      const conditionSignals = condition.signals;
      let conditionMet = true;
      
      conditionSignals.forEach(signal => {
        const received = receivedSignals.find(rs => rs.signal === signal.name);
        
        if (received) {
          completed.push({
            ...signal,
            timestamp: received.timestamp,
            price: received.price,
          });
        } else {
          pending.push(signal);
          if (signal.required) {
            conditionMet = false;
          }
        }
      });
      
      if (conditionMet) {
        isReady = true;
      }
    });
    
    const hasEntry = receivedSignals.some(s => s.type === 'entry');
    const hasExit = receivedSignals.some(s => s.type === 'exit');
    
    setProgress({
      completedSignals: completed,
      pendingSignals: pending,
      isEntryReady: isReady,
      isInPosition: hasEntry && !hasExit,
    });
  };

  const updateTimeRemaining = () => {
    if (!progress.completedSignals.length) return;
    
    const newTimeRemaining = {};
    
    progress.pendingSignals.forEach(signal => {
      const condition = strategy?.conditions.find(c => c.id === signal.conditionId);
      
      if (condition?.type === 'timed') {
        const earlySignal = progress.completedSignals.find(
          s => s.conditionId === signal.conditionId && s.type === 'early'
        );
        
        if (earlySignal) {
          const elapsed = (Date.now() - earlySignal.timestamp) / 1000 / 60;
          const remaining = condition.maxMinutes - elapsed;
          
          if (remaining > 0) {
            newTimeRemaining[signal.id] = Math.ceil(remaining * 60);
          }
        }
      }
    });
    
    setTimeRemaining(newTimeRemaining);
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSignalStatus = (signal) => {
    const isCompleted = progress.completedSignals.some(s => s.id === signal.id);
    const isActive = !isCompleted && timeRemaining[signal.id] > 0;
    const isPending = !isCompleted && !isActive;
    
    return { isCompleted, isActive, isPending };
  };

  if (!strategy) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300">
        Strategy not found: {strategyId}
      </div>
    );
  }

  const totalSignals = progress.completedSignals.length + progress.pendingSignals.length;
  const completionPercent = (progress.completedSignals.length / totalSignals) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <div className={`backdrop-blur-xl bg-gradient-to-br ${strategy.bgColor} border ${strategy.borderColor} rounded-xl overflow-hidden shadow-2xl`}>
        
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-slate-700/30 bg-gradient-to-r from-slate-900/80 to-slate-800/80">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
              <div className="text-3xl md:text-4xl flex-shrink-0">
                {strategy.icon}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg md:text-xl font-bold text-white truncate">
                  {strategy.name}
                </h2>
                <p className="text-xs md:text-sm text-slate-300 truncate">
                  {strategy.description}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {progress.isInPosition && (
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="px-3 py-1 rounded-full bg-green-500/20 border border-green-500/50 text-green-300 text-xs md:text-sm font-bold whitespace-nowrap"
                >
                  🟢 IN POSITION
                </motion.div>
              )}
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-2 hover:bg-slate-700/30 rounded-lg transition-all"
              >
                {expanded ? '▼' : '▶'}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="p-4 md:p-6 space-y-6"
            >
              
              {/* Progress Overview */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-200">Signal Progress</span>
                  <span className="text-xs md:text-sm font-mono font-bold" style={{ color: strategy.color }}>
                    {progress.completedSignals.length}/{totalSignals}
                  </span>
                </div>
                <div className="w-full h-3 bg-slate-700/30 rounded-full overflow-hidden border border-slate-600/30">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${completionPercent}%` }}
                    transition={{ duration: 0.5 }}
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${strategy.color}, ${strategy.color}dd)`,
                      boxShadow: `0 0 12px ${strategy.color}80`,
                    }}
                  />
                </div>
              </div>

              {/* Conditions Grid */}
              <div className="grid grid-cols-1 gap-6">
                {strategy.conditions.map((condition) => (
                  <div key={condition.id}>
                    <h3 className="text-sm md:text-base font-bold text-white mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: strategy.color }}></span>
                      {condition.name}
                    </h3>
                    
                    <div className="space-y-2">
                      {condition.signals.map((signal, idx) => {
                        const { isCompleted, isActive, isPending } = getSignalStatus(signal);
                        const completedSignal = progress.completedSignals.find(s => s.id === signal.id);
                        const isTimeWarning = timeRemaining[signal.id] && timeRemaining[signal.id] < 60;
                        
                        return (
                          <motion.div
                            key={signal.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.05 }}
                          >
                            <div className={`p-3 md:p-4 rounded-lg border transition-all ${
                              isCompleted
                                ? 'bg-green-500/10 border-green-500/30'
                                : isActive
                                  ? 'bg-slate-700/20 border-slate-600/30'
                                  : 'bg-slate-800/40 border-slate-700/20 opacity-60'
                            }`}>
                              <div className="flex items-start gap-3 flex-wrap md:flex-nowrap">
                                
                                {/* Status Icon */}
                                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs md:text-sm" style={{
                                  backgroundColor: isCompleted ? '#00ff8833' : isActive ? `${strategy.color}33` : '#33333366',
                                }}>
                                  {isCompleted ? '✓' : isActive ? '⏳' : '○'}
                                </div>
                                
                                {/* Signal Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="text-sm md:text-base font-bold text-white">
                                      {signal.name}
                                    </span>
                                    {signal.required && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300 font-semibold whitespace-nowrap">
                                        Required
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 mb-2">
                                    {signal.description}
                                  </p>
                                  
                                  {completedSignal && (
                                    <div className="flex gap-2 flex-wrap">
                                      <span className="text-xs px-2 py-1 rounded bg-cyan-500/20 text-cyan-300 font-mono">
                                        {new Date(completedSignal.timestamp).toLocaleTimeString()}
                                      </span>
                                      {completedSignal.price && (
                                        <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-300 font-bold">
                                          ${completedSignal.price.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Timer */}
                                {isActive && timeRemaining[signal.id] && (
                                  <div className="flex-shrink-0 text-center">
                                    <div className="text-xs text-slate-400 mb-1">Expires in</div>
                                    <div className={`font-mono font-bold text-sm md:text-base ${isTimeWarning ? 'text-red-400' : ''}`} style={{ color: isTimeWarning ? '#ff4757' : strategy.color }}>
                                      {formatTime(timeRemaining[signal.id])}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Status Alert */}
              {progress.isEntryReady && !progress.isInPosition && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 flex items-start gap-3"
                >
                  <span className="text-xl flex-shrink-0">✨</span>
                  <div>
                    <div className="font-bold text-green-300 text-sm md:text-base">ENTRY CONDITIONS MET</div>
                    <div className="text-xs text-green-200/80 mt-1">All required signals received. Ready to execute.</div>
                  </div>
                </motion.div>
              )}

              {progress.isInPosition && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-start gap-3"
                >
                  <span className="text-xl flex-shrink-0">📍</span>
                  <div>
                    <div className="font-bold text-cyan-300 text-sm md:text-base">POSITION ACTIVE</div>
                    <div className="text-xs text-cyan-200/80 mt-1">Monitoring for exit signal: {strategy.exitSignal}</div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default StrategyProgressTracker;